/**
 * The two catalogs the Widget module publishes alongside the widgets
 * themselves (CT-5183): presentation types and categories.
 *
 * Both are paginated list endpoints with the same envelope as `/widget/list`,
 * and both are live with real rows since 2026-09-25: 28 presentation types and
 * the three categories.
 *
 * The type catalog is the **source of truth for the vocabulary**. `typeCode` is
 * what a saved widget stores and what `/widget/list` filters on, and the
 * endpoint rejects anything not in this catalog (`404 Widget type X not
 * found`). `rendererKey` is the lowercase name of the drawing, which is what
 * `@aimsos/ui`'s catalog registry keys off.
 */

import { validateApiResponse, normalizePagedMeta } from '../shared/utils/apiResponse.js';
import { apiDashboards, WIDGET_API_PREFIX } from './apiClient.js';
import { cachedRequest } from './requestCache.js';

/** The catalog is 28 rows; one page holds it. */
const DEFAULT_PAGE_SIZE = 100;

/** Presentation type → the option the Configure grid and the TYPE filter render. */
export const mapApiWidgetTypeToUi = (raw = {}) => ({
  // Stored on the widget and sent as `typeCodes` by the filter.
  code: raw.typeCode ?? '',
  // What draws it. The backend's renderer vocabulary and `@aimsos/ui`'s
  // registry agree on 20 of the 28 names outright; the registry carries
  // aliases for the eight that were renamed.
  rendererKey: raw.rendererKey ?? '',
  label: raw.name ?? raw.typeCode ?? '',
  description: raw.about ?? '',
  bestFor: raw.bestFor ?? '',
  icon: raw.icon ?? '',
  categoryKey: raw.categoryKey ?? '',
  baseWidgetType: raw.baseWidgetType ?? '',
  // Which result modes the type accepts. The builder gates on this: a TABLE
  // rejects `SUMMARY` outright (`Widget type TABLE does not support SUMMARY`),
  // so offering it would let the user build something that cannot be saved.
  allowedDataModes: raw.allowedDataModes ?? [],
  allowedSourceTypes: raw.allowedSourceTypes ?? [],
  allowedResultShapes: raw.allowedResultShapes ?? [],
  // Absent means **allowed**, not forbidden. The backend's own rule: "a type
  // without a declaration is accepted (compatibility unknown)" — only an
  // explicit `false` rejects groupers, with
  // `DSW001 Widget type <X> does not support grouping`. Defaulting to `false`
  // would block group-by on a type the backend would have taken.
  supportsGrouping: raw.groupingCapabilities?.supports_grouping !== false,
  supportsUserFilters: raw.filterCapabilities?.supports_user_filters ?? false,
  // JSON Schemas for the type's own configuration and for the shape its data
  // comes back in. Carried whole — nothing reads into them yet.
  configSchema: raw.configSchema ?? null,
  outputSchema: raw.outputSchema ?? null,
  versionNumber: raw.versionNumber ?? null,
  displayOrder: raw.displayOrder ?? 0,
});

/** Widget category → the option the CATEGORY filter renders. */
export const mapApiWidgetCategoryToUi = (raw = {}) => ({
  key: raw.categoryKey ?? raw.key ?? '',
  label: raw.name ?? raw.label ?? raw.categoryKey ?? '',
  description: raw.description ?? '',
  displayOrder: raw.displayOrder ?? 0,
});

/**
 * List the presentation types (`mui_widget_presentation_types`).
 *
 * @param {{ categoryKeys?: string[], dataMode?: string, page?: number, pageSize?: number }} [params]
 */
async function fetchWidgetTypes({
  categoryKeys,
  dataMode,
  page,
  pageSize,
} = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/type/list`, {
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
    ...(categoryKeys?.length ? { categoryKeys } : {}),
    ...(dataMode ? { dataMode } : {}),
  });
  const { body, meta } = validateApiResponse(response, 'Could not load widget types.');
  return {
    // The catalog carries its own order; the Configure grid renders it as given.
    types: (body ?? [])
      .map(mapApiWidgetTypeToUi)
      .sort((a, b) => a.displayOrder - b.displayOrder),
    meta: normalizePagedMeta(meta),
  };
}

/**
 * List the widget categories (`STATISTICS` | `DATA_VIEW` | `CONSUMPTION`).
 * Distinct from the dashboard category catalog.
 */
async function fetchWidgetCategories({ page, pageSize } = {}) {
  const response = await apiDashboards.post(`${WIDGET_API_PREFIX}/category/list_filter`, {
    page: page ?? 1,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
  });
  const { body, meta } = validateApiResponse(response, 'Could not load widget categories.');
  return {
    categories: (body ?? [])
      .map(mapApiWidgetCategoryToUi)
      .sort((a, b) => a.displayOrder - b.displayOrder),
    meta: normalizePagedMeta(meta),
  };
}

/* ── Cached entry points ──────────────────────────────────────────────────
 *
 * Both catalogs are read once per `WidgetPlayground`, which already made them
 * the quiet ones — but "once per mount" is still once per visit, and the
 * builder is opened, left and reopened all day. Reading them through
 * `cachedRequest` makes it once per session, and matches what the data model's
 * lists now do (CT-5579).
 */

/** @see fetchWidgetTypes */
export const listWidgetTypes = (params = {}) => cachedRequest(
  `widgetTypes|${(params.categoryKeys ?? []).join(',')}|${params.dataMode ?? ''}|${params.page ?? ''}|${params.pageSize ?? ''}`,
  () => fetchWidgetTypes(params),
);

/** @see fetchWidgetCategories */
export const listWidgetCategories = (params = {}) => cachedRequest(
  `widgetCategories|${params.page ?? ''}|${params.pageSize ?? ''}`,
  () => fetchWidgetCategories(params),
);
